import { FC, memo, useMemo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import DatePicker from '@amzn/awsui-components-core/date-picker';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import Alert from '@amzn/awsui-components-core/alert';
import { useTranslate } from 'src/hooks/useTranslate';
import { CampaignStatus, CampaignType } from 'src/@types/valueMatching/enums';
import { SectionBaseProps } from '../../types';

/** Hour options (01–12) for 12-hour time picker */
const HOUR_OPTIONS: SelectProps.Option[] = Array.from({ length: 12 }, (_, i) => {
    const val = String(i + 1).padStart(2, '0');
    return { label: val, value: val };
});

/** Minute options (00–59) for time picker */
const MINUTE_OPTIONS: SelectProps.Option[] = Array.from({ length: 60 }, (_, i) => {
    const val = String(i).padStart(2, '0');
    return { label: val, value: val };
});

/** AM/PM options */
const AMPM_OPTIONS: SelectProps.Option[] = [
    { label: 'AM', value: 'AM' },
    { label: 'PM', value: 'PM' },
];

/**
 * Default time applied when a user selects a date but has not yet picked
 * any time value. "12:00" in 24h format = 12:00 PM (noon).
 */
const DEFAULT_TIME = '12:00';

/** Parse "HH:mm" (24h) into { hour12, minute, ampm } */
function parseTime(time: string): { hour: string; minute: string; ampm: string } {
    if (!time || !time.includes(':')) return { hour: '', minute: '', ampm: '' };
    const [hh, mm] = time.split(':');
    const h24 = parseInt(hh, 10);
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return { hour: String(h12).padStart(2, '0'), minute: mm, ampm };
}

/** Combine { hour12, minute, ampm } → "HH:mm" (24h) */
function formatTime(hour: string, minute: string, ampm: string): string {
    if (!hour || !minute || !ampm) return '';
    let h24 = parseInt(hour, 10);
    if (ampm === 'PM' && h24 !== 12) h24 += 12;
    if (ampm === 'AM' && h24 === 12) h24 = 0;
    return `${String(h24).padStart(2, '0')}:${minute}`;
}

export interface SchedulingSectionProps extends SectionBaseProps {
    /** Current start date value (YYYY-MM-DD) */
    startDate: string;
    /** Callback to update the start date */
    setStartDate: (value: string) => void;
    /** Current start time value (hh:mm) */
    startTime: string;
    /** Callback to update the start time */
    setStartTime: (value: string) => void;
    /** Current end date value (YYYY-MM-DD) */
    endDate: string;
    /** Callback to update the end date */
    setEndDate: (value: string) => void;
    /** Current end time value (hh:mm) */
    endTime: string;
    /** Callback to update the end time */
    setEndTime: (value: string) => void;
    /** Propagation delay in minutes from campaign config */
    propagationDelayInMinutes: number;
    /** Display name for the campaign type */
    campaignTypeName: string;
    /** Inline validation error for the start date/time group */
    startDateTimeError?: string;
    /** Inline validation error for the end date/time group */
    endDateTimeError?: string;
    /**
     * Campaign status — in modify mode, if set to anything other than `SCHEDULED`,
     * the start date/time fields are disabled (the campaign has already started
     * or ended, so its start cannot be changed). End date/time remain editable.
     * Ignored in create/view modes.
     */
    campaignStatus?: CampaignStatus;
    /**
     * Campaign type — for DEAL campaigns in modify mode, BOTH start and end
     * date/time fields are fully disabled (DEAL
     * campaigns had their scheduling completely frozen after creation).
     */
    campaignType?: CampaignType;
}

/**
 * Scheduling section — renders start/end date+time fields and a propagation delay alert.
 * Owns its own i18n bundle (`organisms.schedulingSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const SchedulingSection: FC<SchedulingSectionProps> = ({
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    propagationDelayInMinutes,
    campaignTypeName,
    mode,
    disabled,
    startDateTimeError,
    endDateTimeError,
    campaignStatus,
    campaignType,
}) => {
    const { translate } = useTranslate('organisms.schedulingSection');
    const isDisabled = disabled ?? mode === 'view';

    // For DEAL campaigns in modify mode, BOTH start and end date/time are fully frozen.
    const isDealModify = mode === 'modify' && campaignType === CampaignType.DEAL;

    // In modify mode, start date/time are locked once the campaign has actually started.
    // SCHEDULED and PROPAGATING campaigns have NOT started yet, so start time remains editable.
    // For DEAL campaigns, always locked in modify mode regardless of status.
    const hasCampaignStarted = campaignStatus === CampaignStatus.ACTIVE
        || campaignStatus === CampaignStatus.PAUSED_FUNDS_DEPLETED;
    const startFieldsDisabled = isDisabled
        || isDealModify
        || (mode === 'modify' && hasCampaignStarted);

    // End fields are normally editable in modify mode, except for DEAL campaigns
    // where both start and end are frozen.
    const endFieldsDisabled = isDisabled || isDealModify;

    const startParsed = useMemo(() => parseTime(startTime), [startTime]);
    const endParsed = useMemo(() => parseTime(endTime), [endTime]);

    const findOption = (options: SelectProps.Option[], value: string): SelectProps.Option | null =>
        options.find((opt) => opt.value === value) ?? null;

    const handleStartTimeChange = (hour: string, minute: string, ampm: string) => {
        setStartTime(formatTime(hour, minute, ampm));
    };

    const handleEndTimeChange = (hour: string, minute: string, ampm: string) => {
        setEndTime(formatTime(hour, minute, ampm));
    };

    return (
        <Container header={<Header variant="h2">{translate('section_scheduling')}</Header>}>
            <SpaceBetween size="m">
                {mode !== 'view' && propagationDelayInMinutes > 0 && (
                    <Alert type="info">
                        {translate('propagation_delay_alert', {
                            campaignTypeName,
                            propagationDelayInMinutes,
                        })}
                    </Alert>
                )}
                {/* Start date+time (left) | End date+time (right) — grouped in 2 columns */}
                <ColumnLayout columns={2}>
                    <FormField
                        label={translate('start_date_time_label')}
                        errorText={startDateTimeError}
                        i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
                    >
                        <SpaceBetween size="xs" direction="horizontal">
                            <DatePicker
                                value={startDate}
                                onChange={({ detail }) => {
                                    setStartDate(detail.value);

                                    if (detail.value && !startTime) {
                                        setStartTime(DEFAULT_TIME);
                                    }
                                }}
                                placeholder="YYYY-MM-DD"
                                todayAriaLabel="Today"
                                nextMonthAriaLabel="Next month"
                                previousMonthAriaLabel="Previous month"
                                ariaRequired={true}
                                disabled={startFieldsDisabled}
                                invalid={!!startDateTimeError}
                                data-testid="campaign-start-date"
                            />
                            <Select
                                selectedOption={findOption(HOUR_OPTIONS, startParsed.hour)}
                                onChange={({ detail }) => handleStartTimeChange(detail.selectedOption.value ?? '', startParsed.minute || '00', startParsed.ampm || 'AM')}
                                options={HOUR_OPTIONS}
                                placeholder="--"
                                disabled={startFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!startDateTimeError}
                                data-testid="campaign-start-hour"
                            />
                            <Select
                                selectedOption={findOption(MINUTE_OPTIONS, startParsed.minute)}
                                onChange={({ detail }) => handleStartTimeChange(startParsed.hour || '12', detail.selectedOption.value ?? '', startParsed.ampm || 'AM')}
                                options={MINUTE_OPTIONS}
                                placeholder="--"
                                disabled={startFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!startDateTimeError}
                                data-testid="campaign-start-minute"
                            />
                            <Select
                                selectedOption={findOption(AMPM_OPTIONS, startParsed.ampm)}
                                onChange={({ detail }) => handleStartTimeChange(startParsed.hour || '12', startParsed.minute || '00', detail.selectedOption.value ?? 'AM')}
                                options={AMPM_OPTIONS}
                                placeholder="--"
                                disabled={startFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!startDateTimeError}
                                data-testid="campaign-start-ampm"
                            />
                        </SpaceBetween>
                    </FormField>
                    <FormField
                        label={translate('end_date_time_label')}
                        errorText={endDateTimeError}
                        i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
                    >
                        <SpaceBetween size="xs" direction="horizontal">
                            <DatePicker
                                value={endDate}
                                onChange={({ detail }) => {
                                    setEndDate(detail.value);
                                
                                    if (detail.value && !endTime) {
                                        setEndTime(DEFAULT_TIME);
                                    }
                                }}
                                placeholder="YYYY-MM-DD"
                                todayAriaLabel="Today"
                                nextMonthAriaLabel="Next month"
                                previousMonthAriaLabel="Previous month"
                                ariaRequired={true}
                                disabled={endFieldsDisabled}
                                invalid={!!endDateTimeError}
                                data-testid="campaign-end-date"
                            />
                            <Select
                                selectedOption={findOption(HOUR_OPTIONS, endParsed.hour)}
                                onChange={({ detail }) => handleEndTimeChange(detail.selectedOption.value ?? '', endParsed.minute || '00', endParsed.ampm || 'AM')}
                                options={HOUR_OPTIONS}
                                placeholder="--"
                                disabled={endFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!endDateTimeError}
                                data-testid="campaign-end-hour"
                            />
                            <Select
                                selectedOption={findOption(MINUTE_OPTIONS, endParsed.minute)}
                                onChange={({ detail }) => handleEndTimeChange(endParsed.hour || '12', detail.selectedOption.value ?? '', endParsed.ampm || 'AM')}
                                options={MINUTE_OPTIONS}
                                placeholder="--"
                                disabled={endFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!endDateTimeError}
                                data-testid="campaign-end-minute"
                            />
                            <Select
                                selectedOption={findOption(AMPM_OPTIONS, endParsed.ampm)}
                                onChange={({ detail }) => handleEndTimeChange(endParsed.hour || '12', endParsed.minute || '00', detail.selectedOption.value ?? 'AM')}
                                options={AMPM_OPTIONS}
                                placeholder="--"
                                disabled={endFieldsDisabled}
                                ariaRequired={true}
                                invalid={!!endDateTimeError}
                                data-testid="campaign-end-ampm"
                            />
                        </SpaceBetween>
                    </FormField>
                </ColumnLayout>
            </SpaceBetween>
        </Container>
    );
};

export default memo(SchedulingSection);
